import "../../../components/cps-input-with-units";
import "../../../components/custom-color-chip";
import "../../../components/labeled-color-chip";
import "./walk-in-panel";
import CustomColorChip from "../../../components/custom-color-chip";
import WalkInPanel from "./walk-in-panel";
import elt from "../../../html/elt";
import injectCSS from "../../../html/inject-css";
import { CpsInputWithUnits } from "../../../components/cps-input-with-units";
import { DirectionalLightingViewConfig, directionalLightingViewDefaults, materialDefaults, MaterialOptions } from "../../../views/views-config";
import { blackbodyCssGradient } from "../../../color/blackbody";
import { clone, OptionalProperties, update } from "../../../json/optional-properties";
import { deg2rad, rad2deg } from "../../../math/angles";
import { registerListener } from "../../../html/register-listener";

type WalkInStagingPanelConfig = {
    directionalLighting: DirectionalLightingViewConfig,
    wallMaterial: MaterialOptions,
}

const walkInStagingPanelDefaults: WalkInStagingPanelConfig = {
    directionalLighting: directionalLightingViewDefaults,
    wallMaterial: update(clone(materialDefaults), {type: "WallPaint"}),
};

type WalkInStagingPanelOptions = OptionalProperties<WalkInStagingPanelConfig>;

export class WalkInStagingPanel extends WalkInPanel {

    config: WalkInStagingPanelConfig = clone(walkInStagingPanelDefaults);

    lighting: HTMLElement;
    wallColor: HTMLElement;

    constructor(options: WalkInStagingPanelOptions) {
        super();

        injectCSS(STYLESHEET());
        update(this.config, options);

        this.createLightingSection();
        this.createWallColorSection();
    }

    connectedCallback() {
        this.append(this.lighting, this.wallColor);
    }

    createLightingSection() {
        const
            direction = String(Math.round(rad2deg(this.config.directionalLighting.az))),
            temp = String(Math.round(this.config.directionalLighting.temperature));

        let
            directionRange: HTMLInputElement,
            directionInput: CpsInputWithUnits,
            directionReset: HTMLButtonElement,
            tempRange: HTMLInputElement,
            tempInput: CpsInputWithUnits,
            tempReset: HTMLButtonElement;
            
        this.lighting =
            elt('div', { class: 'staging-lighting-block' },
                elt('h5', {}, 'Lighting'),

                directionRange = elt('input',
                    {
                        type: 'range',
                        class: 'form-range',
                        min: '0',
                        max: '360',
                        value: direction,
                    }),
                elt('label', { class: 'form-label' }, 'Direction'),
                directionInput = elt('cps-input-with-units',
                    {
                        units: '°',
                        value: direction,
                    }),
                directionReset = elt(
                    'i',
                    {
                        class: 'fs-3 bi-arrow-counterclockwise',
                        title: 'Reset',
                    }),


                tempRange = elt('input',
                    {
                        type: 'range',
                        class: 'form-range color-temperature-range',
                        min: '1000',
                        max: '9600',
                        value: temp,
                    }),
                elt('label', { class: 'form-label' }, 'Color Temperature'),
                tempInput = elt('cps-input-with-units',
                    {
                        units: 'K',
                        value: temp,
                    }),
                tempReset = elt(
                    'i',
                    {
                        class: 'fs-3 bi-arrow-counterclockwise',
                        title: 'Reset',
                    }),

               );

        registerListener(directionRange, 'input', () => {
            if(directionRange.value != directionInput.value) {
                directionInput.value = directionRange.value;
            }
            const az = deg2rad(Number(directionRange.value));
            // If the azimuth has changed by more than a degree, then we issue
            // an update
            if(Math.abs(this.config.directionalLighting.az - az) > deg2rad(1)) {
                this.config.directionalLighting.az = az;
                this.dispatchEvent(
                    new CustomEvent('global-lighting-input', {bubbles: true}));
            }
        });

        registerListener(directionInput, 'change', () => {
            if(directionRange.value != directionInput.value) {
                directionRange.value = directionInput.value;
            }
            // This could fail with an unhandled exception on bad input, which
            // is fine, it'll just log an error
            const az = deg2rad(Number(directionInput.value));
            if(Math.abs(this.config.directionalLighting.az - az) > deg2rad(1)) {
                this.config.directionalLighting.az = az;
                this.dispatchEvent(
                    new CustomEvent('global-lighting-change', {bubbles: true}));
            }
        });

        registerListener(directionReset, 'click', () => {
            const defaultAz = 259;
            directionRange.value = directionInput.value = String(defaultAz);
            const az = deg2rad(defaultAz);
            this.config.directionalLighting.az = az;
            this.dispatchEvent(
                new CustomEvent('global-lighting-change', {bubbles: true}));
        });

        const updateColorTemperature = (event: InputEvent) => {
            const input = (event.target as HTMLInputElement);
            this.config.directionalLighting.temperature = Number(input.value)
            if(input == tempRange) {
                tempInput.value = input.value;
            } else {
                tempRange.value = input.value;
            }
            this.dispatchEvent(
                new CustomEvent('global-lighting-input', {bubbles: true}));
        };
        registerListener(tempRange, 'input', updateColorTemperature);
        registerListener(tempInput, 'input', updateColorTemperature);
        registerListener(tempReset, 'click', (event: InputEvent) => {
            tempRange.value = tempInput.value = '6500';
            this.config.directionalLighting.temperature = 6500;
            this.dispatchEvent(
                new CustomEvent('global-lighting-input', {bubbles: true}));
        });
    }

    createWallColorSection() {
        const makeColorChip = (color: string, name: string) => {
            const chip = elt('labeled-color-chip',
                             {
                                tint: color,
                                src: '/closet-organizers-images/3D%20Assets/wall1.png',
                             },
                             name);
            registerListener(chip, 'click', () => {
                this.config.wallMaterial.tint = color;
                this.dispatchEvent(
                    new CustomEvent('wall-color-change', {bubbles: true}));
            });
            return chip;
        };

        let custom: CustomColorChip;
        this.wallColor =
            elt('div', { class: 'staging-wall-color-block' },
                elt('h5', {}, 'Wall Color'),
                makeColorChip('#B0B0B0', 'Gray'),
                makeColorChip('#FFFFFF', 'White'),
                makeColorChip('#EBE0BE', 'Tan'),
                custom = elt('custom-color-chip'),
               );
        registerListener(custom, 'change', () => {
            this.config.wallMaterial.tint = custom.tint;
            this.dispatchEvent(
                new CustomEvent('wall-color-change', {bubbles: true}));
        });
        registerListener(custom, 'input', () => {
            this.config.wallMaterial.tint = custom.tint;
            this.dispatchEvent(
                new CustomEvent('wall-color-input', {bubbles: true}));
        });
    }

}
export default WalkInStagingPanel;
window.customElements.define('walk-in-staging-panel', WalkInStagingPanel);

const STYLESHEET = () => {
    const gradient = blackbodyCssGradient();
return `
walk-in-staging-panel {
    & H5 {
        font-size: 1rem;
    }

    & .staging-lighting-block {
        display: grid;
        align-items: center;
        grid-template-columns: 1fr auto auto;
        grid-gap: 0.5rem;
        margin-bottom: 1rem;
        
        & input[type=range] {
            grid-column: 1 / span 3;
        }

        & cps-input-with-units {
            width: 10ch;
        }


        & .color-temperature-range::-webkit-slider-runnable-track
        {
            background: linear-gradient(90deg, ${gradient});
        }

        & .color-temperature-range::-ms-track
        {
            background: linear-gradient(90deg, ${gradient});
        }

        & .color-temperature-range::-moz-range-track
        {
            background: linear-gradient(90deg, ${gradient});
        }

    }

    & .staging-wall-color-block {
        display: grid;
        grid-template-columns: 1fr 1fr 1fr;
        grid-gap: 0.5rem;

        & H5 {
            grid-column: 1 / span 3;
        }
    }
}
`};
